import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { LeadJourneyPanel } from "@/components/admin/LeadJourneyPanel";
import {
  addLeadNote,
  deleteLeadNote,
  importLocalLeadState,
  listAdminLeads,
  setLeadStatus,
  type AdminLead,
  type LeadSource,
} from "@/lib/admin-leads.functions";
import { LEAD_PRIORITIES, LEAD_STATUSES, statusLabel } from "@/lib/leads/topics";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/leads")({
  head: () => ({
    meta: [
      { title: "Leads — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminLeadsPage,
});

const SOURCES: { value: LeadSource | "all"; label: string }[] = [
  // "Alla" = alla kundleads. Karriäransökningar är inte affärsleads och
  // räknas bara in när man aktivt väljer fliken Karriär.
  { value: "all", label: "Alla kundleads" },
  { value: "contact", label: "Kontakt" },
  { value: "quote", label: "Offert" },
  { value: "foto", label: "Foto" },
  { value: "career", label: "Karriär" },
  { value: "callback", label: "Uppringning" },
  { value: "prime", label: "Prime" },
  { value: "partner", label: "Partner" },
  { value: "lead_magnet", label: "Formatguide" },
  { value: "newsletter", label: "Nyhetsbrev" },
];

const RANGES = [
  { value: "1", label: "24h" },
  { value: "7", label: "7d" },
  { value: "30", label: "30d" },
  { value: "all", label: "Allt" },
];

const HANDLED_KEY = "fenny.admin.leads.handled.v1";
const NOTES_KEY = "fenny.admin.leads.notes.v1";
const MIGRATED_KEY = "fenny.admin.leads.migrated.v2";

const STATUS_TONE: Record<string, string> = {
  ny: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  kontaktad: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  offert: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  vunnen: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  forlorad: "bg-muted text-muted-foreground border-border",
};
const STATUS_DOT: Record<string, string> = {
  ny: "bg-amber-400",
  kontaktad: "bg-sky-400",
  offert: "bg-violet-400",
  vunnen: "bg-emerald-400",
  forlorad: "bg-muted-foreground",
};

/** Pipeline-steg som flikar: nya, kontaktade osv hålls isär. */
const STATUS_TABS: { value: string; label: string; dot?: string }[] = [
  { value: "open", label: "Öppna" },
  ...LEAD_STATUSES.map((s) => ({ value: s.value, label: s.label, dot: STATUS_DOT[s.value] })),
  { value: "overdue", label: "Försenade" },
  // "Alla" = alla kundleads. Karriäransökningar är inte affärsleads och
  // räknas bara in när man aktivt väljer fliken Karriär.
  { value: "all", label: "Alla kundleads" },
];


function fmt(d: string) {
  return new Date(d).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
}
function timeAgo(d: string) {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "nyss";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  if (s < 604800) return `${Math.floor(s / 86400)} d`;
  return `${Math.floor(s / 604800)} v`;
}
function isOverdue(date: string | null) {
  if (!date) return false;
  return new Date(`${date}T23:59:59`).getTime() < Date.now();
}
function initials(l: AdminLead) {
  const base = (l.name || l.email || "?").trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function toCsv(rows: AdminLead[]) {
  const headers = [
    "created_at", "source_label", "topic", "name", "email", "phone", "company",
    "budget", "timeline", "value", "status", "owner_email", "follow_up_at", "source_path", "message",
  ];
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc((r as unknown as Record<string, unknown>)[h])).join(",")),
  ].join("\n");
}
function downloadCsv(name: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/* ---------------------------------------------------------------- sida --- */

function AdminLeadsPage() {
  const navigate = useNavigate();
  const fetchLeads = useServerFn(listAdminLeads);
  const saveStatus = useServerFn(setLeadStatus);
  const saveNote = useServerFn(addLeadNote);
  const removeNote = useServerFn(deleteLeadNote);
  const importLocal = useServerFn(importLocalLeadState);

  const [source, setSource] = useState<LeadSource | "all">("all");
  const [range, setRange] = useState<string>("7");
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserEmail(data.user?.email ?? null));
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-leads"],
    queryFn: () => fetchLeads(),
    refetchInterval: 60_000,
  });

  // Engångsflytt av lokala markeringar/anteckningar till databasen.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(MIGRATED_KEY)) return;
    let handled: string[] = [];
    let notes: Record<string, string> = {};
    try { handled = JSON.parse(localStorage.getItem(HANDLED_KEY) ?? "[]"); } catch { /* ignore */ }
    try { notes = JSON.parse(localStorage.getItem(NOTES_KEY) ?? "{}"); } catch { /* ignore */ }
    if (handled.length === 0 && Object.keys(notes).length === 0) {
      localStorage.setItem(MIGRATED_KEY, "1");
      return;
    }
    importLocal({ data: { handled, notes } })
      .then(() => {
        localStorage.setItem(MIGRATED_KEY, "1");
        void refetch();
      })
      .catch(() => { /* försöker igen nästa gång */ });
  }, [importLocal, refetch]);

  const statusMutation = useMutation({
    mutationFn: (vars: {
      leadId: string; status?: string; priority?: string; ownerEmail?: string | null; followUpAt?: string | null;
    }) => saveStatus({ data: vars }),
    onSuccess: () => { void refetch(); },
  });
  const noteMutation = useMutation({
    mutationFn: (vars: { leadId: string; body: string }) => saveNote({ data: vars }),
    onSuccess: () => { void refetch(); },
  });
  const noteDelete = useMutation({
    mutationFn: (noteId: string) => removeNote({ data: { noteId } }),
    onSuccess: () => { void refetch(); },
  });

  const all = data?.leads ?? [];

  const owners = useMemo(
    () => [...new Set(all.map((l) => l.owner_email).filter((x): x is string => Boolean(x)))].sort(),
    [all],
  );

  const dupCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of all) {
      const k = (l.email || l.phone || "").toLowerCase();
      if (!k) continue;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [all]);

  const filtered = useMemo(() => {
    const since = range === "all" ? 0 : Date.now() - Number(range) * 86400_000;
    const ql = q.trim().toLowerCase();
    return all.filter((l) => {
      if (source === "all" ? l.source === "career" : l.source !== source) return false;
      if (statusFilter === "open" && (l.status === "vunnen" || l.status === "forlorad")) return false;
      if (statusFilter === "overdue" && !isOverdue(l.follow_up_at)) return false;
      if (statusFilter !== "all" && statusFilter !== "open" && statusFilter !== "overdue" && l.status !== statusFilter)
        return false;
      if (ownerFilter === "mine" && l.owner_email !== userEmail) return false;
      if (ownerFilter !== "all" && ownerFilter !== "mine" && l.owner_email !== ownerFilter) return false;
      if (since && new Date(l.created_at).getTime() < since) return false;
      if (ql) {
        // Sök i allt vi vet om leadet: kontaktuppgifter, ämne, meddelande,
        // formulär, landningssida, detaljfält, anteckningar och rådata.
        const blob = [
          l.email, l.name, l.phone, l.company, l.topic, l.message,
          l.source_path, l.source_label, l.intent, l.budget, l.timeline,
          l.owner_email, l.status, l.raw, l.summary,
          l.details.map((d) => `${d.label} ${d.value}`).join(" "),
          l.notes.map((n) => n.body).join(" "),
        ].join(" ").toLowerCase();
        const terms = ql.split(/\s+/).filter(Boolean);
        if (!terms.every((t) => blob.includes(t))) return false;
      }
      return true;
    });
  }, [all, source, range, q, statusFilter, ownerFilter, userEmail]);

  const counts = useMemo(() => {
    const since = range === "all" ? 0 : Date.now() - Number(range) * 86400_000;
    const c: Record<string, number> = { all: 0 };
    for (const l of all) {
      if (since && new Date(l.created_at).getTime() < since) continue;
      if (l.source !== "career") c.all = (c.all ?? 0) + 1;
      c[l.source] = (c[l.source] ?? 0) + 1;
    }
    return c;
  }, [all, range]);

  const kpis = useMemo(() => {
    const day = 86400_000;
    const now = Date.now();
    // KPI:erna mäter affären — karriäransökningar hålls utanför.
    const customer = all.filter((l) => l.source !== "career");
    const last24 = customer.filter((l) => now - new Date(l.created_at).getTime() <= day).length;
    const nya = customer.filter((l) => l.status === "ny").length;
    const overdue = customer.filter((l) => isOverdue(l.follow_up_at) && l.status !== "vunnen" && l.status !== "forlorad").length;
    const obemannade = customer.filter((l) => !l.owner_email && l.status !== "vunnen" && l.status !== "forlorad").length;
    const vunna = customer.filter((l) => l.status === "vunnen").length;
    const quoteValue = customer
      .filter((l) => l.source === "quote" && l.value)
      .reduce((s, l) => s + (l.value ?? 0), 0);
    return { last24, nya, overdue, obemannade, vunna, total: customer.length, quoteValue };
  }, [all]);

  // Antal per pipeline-steg, inom valt tidsspann/källa/ansvarig men oberoende av statusfliken.
  const statusCounts = useMemo(() => {
    const since = range === "all" ? 0 : Date.now() - Number(range) * 86400_000;
    const c: Record<string, number> = { all: 0, open: 0, overdue: 0 };
    for (const l of all) {
      if (source === "all" ? l.source === "career" : l.source !== source) continue;
      if (ownerFilter === "mine" && l.owner_email !== userEmail) continue;
      if (ownerFilter !== "all" && ownerFilter !== "mine" && l.owner_email !== ownerFilter) continue;
      if (since && new Date(l.created_at).getTime() < since) continue;
      c.all = (c.all ?? 0) + 1;
      c[l.status] = (c[l.status] ?? 0) + 1;
      if (l.status !== "vunnen" && l.status !== "forlorad") c.open = (c.open ?? 0) + 1;
      if (isOverdue(l.follow_up_at)) c.overdue = (c.overdue ?? 0) + 1;
    }
    return c;
  }, [all, source, range, ownerFilter, userEmail]);

  // Gruppera träffarna per status så att nya, kontaktade osv aldrig blandas i listan.
  const grouped = useMemo(() => {
    const order = LEAD_STATUSES.map((s) => s.value);
    const map = new Map<string, AdminLead[]>();
    for (const l of filtered) {
      const arr = map.get(l.status) ?? [];
      arr.push(l);
      map.set(l.status, arr);
    }
    return [...map.entries()]
      .sort((a, b) => order.indexOf(a[0] as (typeof order)[number]) - order.indexOf(b[0] as (typeof order)[number]))
      .map(([status, leads]) => ({ status, leads }));
  }, [filtered]);

  const openLead = useMemo(() => filtered.find((l) => l.id === openId) ?? null, [filtered, openId]);


  // Tangentbord: j/k stegar i listan, Enter öppnar, Esc stänger, / söker.
  const move = useCallback((dir: 1 | -1) => {
    if (filtered.length === 0) return;
    const i = filtered.findIndex((l) => l.id === openId);
    const next = i === -1 ? 0 : Math.min(filtered.length - 1, Math.max(0, i + dir));
    setOpenId(filtered[next]!.id);
  }, [filtered, openId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) {
        if (e.key === "Escape") t.blur();
        return;
      }
      if (e.key === "j" || e.key === "ArrowDown") { e.preventDefault(); move(1); }
      else if (e.key === "k" || e.key === "ArrowUp") { e.preventDefault(); move(-1); }
      else if (e.key === "Escape") setOpenId(null);
      else if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move]);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  async function bulkStatus(status: string) {
    for (const id of selected) {
      await statusMutation.mutateAsync({ leadId: id, status });
    }
    clearSelection();
  }
  function exportRows() {
    const rows = selected.size === 0 ? filtered : filtered.filter((l) => selected.has(l.id));
    downloadCsv(`leads-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows));
  }
  function bulkMailto() {
    const rows = (selected.size === 0 ? filtered : filtered.filter((l) => selected.has(l.id)))
      .map((l) => l.email).filter(Boolean);
    if (rows.length === 0) return;
    window.location.href = `mailto:?bcc=${[...new Set(rows)].join(",")}`;
  }

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1200);
    });
  }

  async function logout() {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  }

  const busy = statusMutation.isPending || noteMutation.isPending;

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky arbetsyta-header: sökning alltid nåbar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-3 px-4 py-3">
          <div className="mr-2">
            <h1 className="text-lg font-semibold leading-none text-foreground">Leads</h1>
            <p className="mt-1 text-[11px] text-muted-foreground">{userEmail ?? "—"}</p>
          </div>

          <div className="relative min-w-[220px] flex-1">
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sök namn, e-post, företag, meddelande, sida…"
              className="w-full rounded-full border border-border bg-card/40 px-4 py-2 pr-10 text-sm outline-none focus:border-mint/60"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border px-1.5 text-[10px] text-muted-foreground">/</kbd>
          </div>

          <div className="flex items-center gap-1 rounded-full border border-border p-0.5">
            {RANGES.map((r) => (
              <button key={r.value} onClick={() => setRange(r.value)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${range === r.value ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
                {r.label}
              </button>
            ))}
          </div>

          <button onClick={() => refetch()} className="rounded-full border border-border px-3 py-1.5 text-xs hover:border-mint/50">
            {isFetching ? "Uppdaterar…" : "Uppdatera"}
          </button>
          <button onClick={logout} className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
            Logga ut
          </button>
        </div>

        {/* KPI:er som filtergenvägar */}
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-2 px-4 pb-3">
          <KpiChip label="Nya" value={kpis.nya} tone="amber" active={statusFilter === "ny"} onClick={() => setStatusFilter(statusFilter === "ny" ? "open" : "ny")} />
          <KpiChip label="Försenade" value={kpis.overdue} tone="red" active={statusFilter === "overdue"} onClick={() => setStatusFilter(statusFilter === "overdue" ? "open" : "overdue")} />
          <KpiChip label="Utan ansvarig" value={kpis.obemannade} tone="plain" active={false} onClick={() => setOwnerFilter("all")} />
          <KpiChip label="Senaste dygnet" value={kpis.last24} tone="plain" active={range === "1"} onClick={() => setRange("1")} />
          <KpiChip label="Vunna" value={kpis.vunna} tone="emerald" active={statusFilter === "vunnen"} onClick={() => setStatusFilter(statusFilter === "vunnen" ? "open" : "vunnen")} />
          {kpis.quoteValue > 0 && (
            <span className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground">
              Offertvärde <span className="text-foreground">{kpis.quoteValue.toLocaleString("sv-SE")} kr</span>
            </span>
          )}
        </div>
      </header>

      {error && (
        <div className="mx-auto mt-4 max-w-[1600px] px-4">
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {error instanceof Error ? error.message : "Kunde inte hämta leads"}
          </div>
        </div>
      )}

      <div className="mx-auto grid max-w-[1600px] gap-4 px-4 py-4 lg:grid-cols-[minmax(340px,420px)_minmax(0,1fr)]">
        {/* ------------------------------------------------------- lista --- */}
        <section className="flex min-h-0 flex-col rounded-2xl border border-border bg-card/20">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border p-2">
            {SOURCES.map((s) => (
              <button key={s.value} onClick={() => setSource(s.value)}
                className={`rounded-full px-2.5 py-1 text-[11px] transition-colors ${source === s.value ? "bg-mint text-mint-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}>
                {s.label}{counts[s.value] ? ` ${counts[s.value]}` : ""}
              </button>
            ))}
          </div>

          {/* Statussteg som egna flikar — nya och kontaktade blandas inte ihop */}
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border p-2">
            {STATUS_TABS.map((t) => {
              const active = statusFilter === t.value;
              return (
                <button key={t.value} onClick={() => setStatusFilter(t.value)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] transition-colors ${active ? "border border-mint/60 bg-mint/15 text-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}>
                  {t.dot && <span className={`size-1.5 rounded-full ${t.dot}`} />}
                  {t.label}
                  <span className={active ? "text-foreground" : "opacity-70"}>{statusCounts[t.value] ?? 0}</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-border px-2 py-2 text-[11px]">

            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
              className="rounded-full border border-border bg-background px-2.5 py-1">
              <option value="all">Alla ansvariga</option>
              <option value="mine">Mina</option>
              {owners.map((o) => <option key={o} value={o}>{o.split("@")[0]}</option>)}
            </select>
            <span className="ml-auto text-muted-foreground">{filtered.length} träffar</span>
          </div>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-mint/10 px-2 py-2 text-[11px]">
              <span className="text-foreground">{selected.size} markerade</span>
              <button onClick={() => bulkStatus("kontaktad")} className="rounded-full border border-border px-2.5 py-1 hover:border-mint/50">Kontaktade</button>
              <button onClick={() => bulkStatus("forlorad")} className="rounded-full border border-border px-2.5 py-1 hover:border-mint/50">Förlorade</button>
              <button onClick={bulkMailto} className="rounded-full border border-border px-2.5 py-1 hover:border-mint/50">Mejla</button>
              <button onClick={exportRows} className="rounded-full border border-border px-2.5 py-1 hover:border-mint/50">CSV</button>
              <button onClick={clearSelection} className="ml-auto text-muted-foreground hover:text-foreground">Avmarkera</button>
            </div>
          )}

          <div className="max-h-[calc(100vh-16rem)] min-h-[240px] overflow-y-auto">
            {isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Laddar…</p>}
            {!isLoading && filtered.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-sm text-foreground">Inga leads matchar filtren.</p>
                <button onClick={() => { setSource("all"); setStatusFilter("all"); setOwnerFilter("all"); setRange("all"); setQ(""); }}
                  className="mt-3 rounded-full border border-border px-3 py-1.5 text-xs hover:border-mint/50">
                  Nollställ filter
                </button>
              </div>
            )}
            {grouped.map((g) => (
              <div key={g.status}>
                {/* Tydlig avdelare per steg i pipen */}
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/95 px-3 py-1.5 backdrop-blur">
                  <span className={`size-1.5 rounded-full ${STATUS_DOT[g.status] ?? "bg-muted-foreground"}`} />
                  <span className="text-[11px] font-medium uppercase tracking-wide text-foreground">{statusLabel(g.status)}</span>
                  <span className="text-[11px] text-muted-foreground">{g.leads.length}</span>
                </div>
                <ul>
                  {g.leads.map((l) => (
                    <LeadListItem
                      key={l.id}
                      l={l}
                      active={openId === l.id}
                      isSelected={selected.has(l.id)}
                      dup={dupCounts.get((l.email || l.phone || "").toLowerCase()) ?? 0}
                      onOpen={() => setOpenId(l.id)}
                      onSelect={() => toggleSelect(l.id)}
                    />
                  ))}
                </ul>
              </div>
            ))}

          </div>

          <div className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
            <kbd className="rounded border border-border px-1">J</kbd> / <kbd className="rounded border border-border px-1">K</kbd> bläddra ·{" "}
            <kbd className="rounded border border-border px-1">/</kbd> sök · <kbd className="rounded border border-border px-1">Esc</kbd> stäng
            <button onClick={exportRows} className="float-right hover:text-foreground">Exportera CSV</button>
          </div>
        </section>

        {/* ------------------------------------------------------ detalj --- */}
        <section className="min-w-0">
          {openLead ? (
            <LeadDetail
              key={openLead.id}
              l={openLead}
              busy={busy}
              currentUser={userEmail}
              copiedKey={copiedKey}
              onCopy={copy}
              onClose={() => setOpenId(null)}
              onStatus={(patch) => statusMutation.mutate({ leadId: openLead.id, ...patch })}
              onNote={(body) => noteMutation.mutate({ leadId: openLead.id, body })}
              onDeleteNote={(id) => noteDelete.mutate(id)}
            />
          ) : (
            <div className="flex h-full min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-border p-10 text-center">
              <div>
                <p className="text-sm text-foreground">Välj ett lead i listan</p>
                <p className="mt-1 text-xs text-muted-foreground">Allt om kontakten — meddelande, mejl, anteckningar och besöksresa — visas här.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ delkomp. --- */

function KpiChip({ label, value, tone, active, onClick }: {
  label: string; value: number; tone: "amber" | "red" | "emerald" | "plain"; active: boolean; onClick: () => void;
}) {
  const toneClass =
    value === 0 ? "text-muted-foreground"
      : tone === "amber" ? "text-amber-300"
      : tone === "red" ? "text-red-400"
      : tone === "emerald" ? "text-emerald-400"
      : "text-foreground";
  return (
    <button onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${active ? "border-mint/60 bg-mint/10" : "border-border hover:border-mint/40"}`}>
      <span className="text-muted-foreground">{label}</span>{" "}
      <span className={`font-semibold ${toneClass}`}>{value}</span>
    </button>
  );
}

function mailStatusBadge(status: string) {
  const ok = status === "sent";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
      {ok ? "skickat" : "fel"}
    </span>
  );
}

/** Gör länkar, telefonnummer och mejladresser klickbara i leaddetaljerna. */
function renderValue(value: string) {
  const v = value.trim();
  if (/^https?:\/\/\S+$/i.test(v)) {
    return <a href={v} target="_blank" rel="noreferrer noopener" className="break-all underline">{v}</a>;
  }
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return <a href={`mailto:${v}`} className="underline">{v}</a>;
  if (/^\+?[\d\s()-]{7,20}$/.test(v)) return <a href={`tel:${v.replace(/\s/g, "")}`} className="underline">{v}</a>;
  return <>{v}</>;
}

const HIDDEN_RAW_KEYS = new Set([
  "id", "created_at", "name", "email", "message", "company", "intent", "source_path",
  "budget", "timeline", "visitor_id", "session_id", "phone", "magnet_slug", "period",
  "channels", "filmdays", "clips_per_month", "extra_formats", "addons", "total_amount",
  "monthly_amount", "currency",
]);

const RAW_LABELS: Record<string, string> = {
  user_agent: "Enhet / webbläsare",
  utm_source: "Kanal (utm_source)",
  utm_medium: "Medium (utm_medium)",
  utm_campaign: "Kampanj (utm_campaign)",
  funnel_id: "Funnel",
};

/** Alla sparade fält som inte redan visas någon annanstans i detaljvyn. */
function extraFields(l: AdminLead): { label: string; value: string }[] {
  let raw: Record<string, unknown> = {};
  try { raw = JSON.parse(l.raw) as Record<string, unknown>; } catch { return []; }
  const out: { label: string; value: string }[] = [];
  for (const [k, v] of Object.entries(raw)) {
    if (HIDDEN_RAW_KEYS.has(k)) continue;
    if (v === null || v === undefined || v === "") continue;
    const value = Array.isArray(v) ? v.join(", ") : String(v);
    if (!value.trim()) continue;
    out.push({ label: RAW_LABELS[k] ?? k.replace(/_/g, " "), value });
  }
  if (l.visitor_id) out.push({ label: "Besökar-id", value: l.visitor_id });
  if (l.session_id) out.push({ label: "Sessions-id", value: l.session_id });
  return out;
}

function LeadListItem({ l, active, isSelected, dup, onOpen, onSelect }: {
  l: AdminLead; active: boolean; isSelected: boolean; dup: number; onOpen: () => void; onSelect: () => void;
}) {
  const overdue = isOverdue(l.follow_up_at) && l.status !== "vunnen" && l.status !== "forlorad";
  const closed = l.status === "vunnen" || l.status === "forlorad";
  const failed = l.emails.filter((e) => e.status !== "sent").length;

  return (
    <li className={`group border-b border-border/60 ${active ? "bg-mint/10" : "hover:bg-muted/20"} ${closed ? "opacity-65" : ""}`}>
      <div className="flex items-start gap-2 px-2 py-2.5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          aria-label="Markera lead"
          className="mt-1.5 accent-mint opacity-40 transition-opacity group-hover:opacity-100"
        />
        <button onClick={onOpen} className="flex min-w-0 flex-1 items-start gap-3 text-left">
          <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
            {initials(l)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className={`size-1.5 shrink-0 rounded-full ${STATUS_DOT[l.status] ?? "bg-muted-foreground"}`} />
              <span className="truncate text-sm font-medium text-foreground">{l.name || l.email || "Okänd"}</span>
              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{timeAgo(l.created_at)}</span>
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="rounded-full bg-muted px-1.5 py-0.5">{l.source_label}</span>
              <span className="truncate">{l.topic}</span>
            </span>
            {l.message && <span className="mt-1 block truncate text-[11px] text-muted-foreground/80">{l.message}</span>}
            <span className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
              {overdue && <span className="text-red-400">Försenad uppföljning</span>}
              {l.priority === "hog" && <span className="text-amber-400">Hög prio</span>}
              {failed > 0 && <span className="text-red-400">{failed} mejlfel</span>}
              {dup > 1 && <span className="text-amber-400">{dup} leads från samma</span>}
              {l.owner_email && <span className="text-muted-foreground">{l.owner_email.split("@")[0]}</span>}
            </span>
          </span>
        </button>
      </div>
    </li>
  );
}

const TABS = ["Förfrågan", "Aktivitet", "Mejl", "Besöksresa", "Rådata"] as const;
type Tab = (typeof TABS)[number];

function LeadDetail({ l, busy, currentUser, copiedKey, onCopy, onClose, onStatus, onNote, onDeleteNote }: {
  l: AdminLead;
  busy: boolean;
  currentUser: string | null;
  copiedKey: string | null;
  onCopy: (t: string, k: string) => void;
  onClose: () => void;
  onStatus: (patch: { status?: string; priority?: string; ownerEmail?: string | null; followUpAt?: string | null }) => void;
  onNote: (body: string) => void;
  onDeleteNote: (id: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("Förfrågan");
  const [noteDraft, setNoteDraft] = useState("");
  const overdue = isOverdue(l.follow_up_at) && l.status !== "vunnen" && l.status !== "forlorad";

  let pretty = l.raw;
  try { pretty = JSON.stringify(JSON.parse(l.raw), null, 2); } catch { /* keep */ }

  const replyBody = encodeURIComponent(
    `Hej${l.name ? ` ${l.name.split(" ")[0]}` : ""},\n\nTack för att du hörde av dig om ${l.topic.toLowerCase()}.\n\n`,
  );

  const timeline = [
    { at: l.created_at, text: `Lead inkom · ${l.source_label}` },
    ...l.emails.map((e) => ({ at: e.sent_at, text: `Mejl "${e.subject}" till ${e.recipient_email} (${e.status})` })),
    ...l.events.map((e) => ({
      at: e.created_at,
      text: `${e.kind === "note" ? "Anteckning" : "Ändring"}${e.detail ? `: ${e.detail}` : ""}${e.actor_email ? ` — ${e.actor_email}` : ""}`,
    })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));

  const extras = extraFields(l);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card/20">
      {/* Kontaktkort */}
      <div className="border-b border-border p-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-muted text-sm font-semibold">{initials(l)}</span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold text-foreground">{l.name || l.email || "Okänd kontakt"}</h2>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span className="rounded-full bg-muted px-2 py-0.5 text-foreground">{l.source_label}</span>
              <span>{l.topic}</span>
              <span>·</span>
              <span>{fmt(l.created_at)}</span>
              {l.company && <><span>·</span><span>{l.company}</span></>}
            </p>
          </div>
          <button onClick={onClose} className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
            Stäng
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {l.email && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5">
              <a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a>
              <button onClick={() => onCopy(l.email, `e:${l.id}`)} className="text-muted-foreground hover:text-foreground" aria-label="Kopiera e-post">
                {copiedKey === `e:${l.id}` ? "✓" : "⧉"}
              </button>
            </span>
          )}
          {l.phone && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5">
              <a href={`tel:${l.phone}`} className="hover:underline">{l.phone}</a>
              <button onClick={() => onCopy(l.phone!, `p:${l.id}`)} className="text-muted-foreground hover:text-foreground" aria-label="Kopiera telefon">
                {copiedKey === `p:${l.id}` ? "✓" : "⧉"}
              </button>
            </span>
          )}
          {l.email && (
            <a className="rounded-full bg-mint px-3 py-1.5 font-medium text-mint-foreground"
              href={`mailto:${l.email}?subject=${encodeURIComponent(`Re: ${l.topic} — Fenny`)}&body=${replyBody}`}>
              Svara på mejl
            </a>
          )}
          <Link to="/admin/bokningar" className="rounded-full border border-border px-3 py-1.5 hover:border-mint/50">Föreslå mötestid</Link>
          {l.source_path && /^\/[^\s]*$/.test(l.source_path) && (
            <a className="rounded-full border border-border px-3 py-1.5 hover:border-mint/50" href={l.source_path} target="_blank" rel="noreferrer noopener">
              Öppna källsida
            </a>
          )}
        </div>
      </div>

      {/* Arbetsrad: status, prio, ansvarig, uppföljning */}
      <div className="grid gap-3 border-b border-border p-4 sm:grid-cols-2 xl:grid-cols-4">
        <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Status
          <select value={l.status} disabled={busy} onChange={(e) => onStatus({ status: e.target.value })}
            className={`mt-1 w-full rounded-full border px-3 py-1.5 text-xs normal-case tracking-normal ${STATUS_TONE[l.status] ?? "border-border bg-muted"}`}>
            {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Prioritet
          <select value={l.priority} disabled={busy} onChange={(e) => onStatus({ priority: e.target.value })}
            className="mt-1 w-full rounded-full border border-border bg-background px-3 py-1.5 text-xs normal-case tracking-normal">
            {LEAD_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Ansvarig
          {l.owner_email ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="rounded-full border border-border px-3 py-1.5 text-xs normal-case tracking-normal text-foreground">{l.owner_email}</span>
              <button onClick={() => onStatus({ ownerEmail: null })} className="text-[11px] normal-case hover:text-foreground">Släpp</button>
            </div>
          ) : (
            <button onClick={() => onStatus({ ownerEmail: currentUser })}
              className="mt-1 w-full rounded-full border border-border px-3 py-1.5 text-xs normal-case tracking-normal text-foreground hover:border-mint/50">
              Ta ansvar
            </button>
          )}
        </div>
        <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Uppföljning
          <input type="date" value={l.follow_up_at ?? ""} onChange={(e) => onStatus({ followUpAt: e.target.value || null })}
            className={`mt-1 w-full rounded-full border bg-background px-3 py-1.5 text-xs normal-case tracking-normal ${overdue ? "border-red-500/50 text-red-400" : "border-border"}`} />
        </label>
      </div>

      {/* Flikar */}
      <div className="flex gap-1 overflow-x-auto border-b border-border px-3 py-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${tab === t ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            {t}{t === "Mejl" && l.emails.length ? ` (${l.emails.length})` : ""}
            {t === "Aktivitet" && l.notes.length ? ` (${l.notes.length})` : ""}
          </button>
        ))}
      </div>

      <div className="max-h-[calc(100vh-24rem)] min-h-[220px] overflow-y-auto p-4">
        {tab === "Förfrågan" && (
          <div className="space-y-4">
            {l.message ? (
              <p className="whitespace-pre-wrap rounded-xl border border-border bg-background p-4 text-sm text-foreground">{l.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Inget meddelande skrevs i formuläret.</p>
            )}

            {l.details.length > 0 && (
              <FieldGrid title="Uppgifter från formuläret" rows={l.details} />
            )}

            <FieldGrid
              title="Ursprung"
              rows={[
                ...(l.source_path ? [{ label: "Källsida", value: l.source_path }] : []),
                { label: "Inkom", value: fmt(l.created_at) },
                ...(l.intent ? [{ label: "Kod", value: l.intent }] : []),
              ]}
            />

            {extras.length > 0 && <FieldGrid title="Teknik och spårning" rows={extras} muted />}
          </div>
        )}

        {tab === "Aktivitet" && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border p-3">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={3}
                placeholder="Vad hände i kontakten?"
                className="w-full resize-y rounded-lg border border-border bg-background p-2 text-sm outline-none focus:border-mint/60"
              />
              <div className="mt-2 flex justify-end">
                <button
                  disabled={!noteDraft.trim() || busy}
                  onClick={() => { onNote(noteDraft.trim()); setNoteDraft(""); }}
                  className="rounded-full bg-mint px-4 py-1.5 text-xs font-medium text-mint-foreground disabled:opacity-40"
                >
                  Spara anteckning
                </button>
              </div>
            </div>

            <ul className="space-y-2">
              {l.notes.length === 0 && <li className="text-xs text-muted-foreground">Inga anteckningar än.</li>}
              {l.notes.map((n) => (
                <li key={n.id} className="rounded-xl border border-border p-3 text-sm">
                  <p className="whitespace-pre-wrap text-foreground">{n.body}</p>
                  <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{n.author_email ?? "okänd"} · {fmt(n.created_at)}</span>
                    <button onClick={() => onDeleteNote(n.id)} className="hover:text-red-400">Ta bort</button>
                  </div>
                </li>
              ))}
            </ul>

            <div>
              <h3 className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Historik</h3>
              <ol className="space-y-2 border-l border-border pl-4">
                {timeline.map((t, i) => (
                  <li key={`${t.at}-${i}`} className="relative text-[11px] text-muted-foreground">
                    <span className="absolute -left-[21px] top-1.5 size-1.5 rounded-full bg-border" />
                    <span className="text-foreground">{fmt(t.at)}</span> · {t.text}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {tab === "Mejl" && (
          <ul className="space-y-2">
            {l.emails.length === 0 && <li className="text-xs text-muted-foreground">Inga mejl loggade för {l.email || "detta lead"}.</li>}
            {l.emails.map((e) => (
              <li key={e.id} className="rounded-xl border border-border p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{e.subject}</span>
                  {mailStatusBadge(e.status)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{e.kind} · till {e.recipient_email} · {fmt(e.sent_at)}</div>
                {e.error && <div className="mt-1 text-xs text-red-400">{e.error}</div>}
              </li>
            ))}
          </ul>
        )}

        {tab === "Besöksresa" && <LeadJourneyPanel visitorId={l.visitor_id} email={l.email} />}

        {tab === "Rådata" && (
          <pre className="overflow-auto rounded-xl border border-border bg-background p-3 text-[11px] text-muted-foreground">{pretty}</pre>
        )}
      </div>

      <p className="border-t border-border px-4 py-2 text-[10px] text-muted-foreground">
        Status: {statusLabel(l.status)}{l.status_updated_at ? ` · uppdaterad ${fmt(l.status_updated_at)}` : ""}
      </p>
    </div>
  );
}

function FieldGrid({ title, rows, muted }: { title: string; rows: { label: string; value: string }[]; muted?: boolean }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h3 className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">{title}</h3>
      <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {rows.map((d) => (
          <div key={`${d.label}-${d.value}`} className="min-w-0">
            <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{d.label}</dt>
            <dd className={`break-words text-xs ${muted ? "text-muted-foreground" : "text-foreground"}`}>{renderValue(d.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
